/**
 * CreateRecipe - the lye calculator.
 *
 * Simple by default, advanced one click away:
 * - Left: 1 batch size, 2 oils, 3 fragrance & additives (closed), advanced settings (closed,
 *   with its current values in one line), then name and save.
 * - Right: results that stay on screen, bar qualities, and costs & pricing (closed).
 * The calculation itself lives in ./utils/recipeCalculations and is unchanged by the layout.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Link } from 'react-router-dom';
import { subscribeToUserCollection, addUserDoc, updateUserDoc } from "../utils/userDataHelper";
import { normalizeRecipe, gramsToWeight, weightToGrams } from '../utils/recipeSchema';
import { calculateRecipe, mergeOilCatalogue } from './utils/recipeCalculations';
import { useAuth } from '../contexts/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../utils/firebase-config';
import { num, computeMaterialCost, buildTotals, computePricing, getCostPerUnit, getUnit } from "./utils/costCalculations";
import useCurrencyTools from "../hooks/useCurrencyTools";
import { RecipePrintSheet } from './PrintCenter';
import "../styles/calculator.css";
import "./labcss/lye-calculator.css";

export default function CreateRecipe({ ingredients = [], selectedRecipe }) {
  // ============ STATE ============
  const isDirtyRef = useRef(false);
  const { preferredCurrency, formatMoney, convertMoney } = useCurrencyTools();
  const { user } = useAuth();
  const [loadedRecipeId, setLoadedRecipeId] = useState(null);
  const [message, setMessage] = useState('');
  const [showPrint, setShowPrint] = useState(false);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const [recipe, setRecipe] = useState({
    schemaVersion: 2,
    name: "",
    notes: "",
    items: [],
    additives: [],
    use: ["body"],
    skinType: ["all"],
    goal: "balanced",
    superfat: 5,
    lyeConcentration: 0.33,
    waterPercent: 38,
    waterMethod: 'percent', // 'percent' | 'ratio' | 'concentration'
    waterLyeRatio: 2,
    waterDiscount: 0,
    lyeType: 'NaOH',
    lyePurity: 97,
    kohPurity: 97,
    dualLye: false,
    kohRatio: 50,
    totalOilWeight: 1000,
    unit: 'g',
    batchSize: 1000,
    barWeight: 100,
    cost: { material: 0, working: 0, packaging: 0 },
    packagingCostPerBar: 5,
    laborCostPerBar: 10,
    markupPercent: 120,
    alkali: { name: "", key: "" },
    makePrivate: true,
    createTemplate: false,
  });

  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [recentRecipes, setRecentRecipes] = useState([]);
  const [newAdditive, setNewAdditive] = useState({ id: '', name: '', amount: '', unit: '', type: 'fragrance' });
  const [packagingItems, setPackagingItems] = useState([]);
  const [selectedPackagingId, setSelectedPackagingId] = useState('');
  const [selectedAlkaliId, setSelectedAlkaliId] = useState('');
  const [selectedKohId, setSelectedKohId] = useState('');

  // Load packaging inventory
  useEffect(() => {
    if (!user) { setPackagingItems([]); return undefined; }
    let unsub = () => {};
    try {
      unsub = subscribeToUserCollection('packaging', (data) => {
        const withId = data.map(item => ({ ...item, firebaseId: item.id }));
        setPackagingItems(withId);
      });
    } catch (err) {
      console.error('Packaging load failed', err);
    }
    return () => unsub();
  }, [user]);

  // Filter ingredients by category
  const oilList = useMemo(() => mergeOilCatalogue(ingredients), [ingredients]);

  const additiveOptions = useMemo(() => {
    return (ingredients || []).filter(ing => {
      const cat = (ing.category || ing.type || '').toLowerCase();
      return ['additive', 'clay', 'herb', 'scrub', 'liquid', 'functional', 'colour', 'color', 'fragrance', 'essential'].some(k => cat.includes(k));
    }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [ingredients]);

  // Filtered oils based on search
  const filteredOils = useMemo(() => {
    if (!searchTerm) return oilList;
    return oilList.filter(oil => 
      oil.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [oilList, searchTerm]);

  const handleAddAdditive = () => {
    if (!newAdditive.id || !newAdditive.amount) return;
    setRecipe(prev => ({
      ...prev,
      additives: [...(prev.additives || []), { ...newAdditive }],
    }));
    setNewAdditive({ id: '', name: '', amount: '', unit: '', type: 'fragrance' });
    isDirtyRef.current = true;
  };

  const handleRemoveAdditive = (idx) => {
    setRecipe(prev => ({
      ...prev,
      additives: (prev.additives || []).filter((_, i) => i !== idx),
    }));
    isDirtyRef.current = true;
  };

  // ============ CALCULATIONS ============
  const calculations = useMemo(() => calculateRecipe(recipe, ingredients), [recipe, ingredients]);
  const bars = calculations.bars;
  const hasValidRecipe = calculations.valid;
  const pricedIngredients = useMemo(() => ingredients.map(item => ({
    ...item,
    costPerUnit: convertMoney(getCostPerUnit(item), item.currencyCode || preferredCurrency, preferredCurrency),
  })), [ingredients, preferredCurrency, convertMoney]);
  const materialCost = useMemo(() => computeMaterialCost(
    pricedIngredients, calculations.oils, calculations.lyeRequired,
    calculations.waterWeight, calculations.additives,
    { wastageBuffer: 0.05, alkaliId: selectedAlkaliId, lyeType: recipe.lyeType,
      alkaliWeights: calculations.alkaliWeights,
      alkaliIds: recipe.dualLye ? { NaOH: selectedAlkaliId, KOH: selectedKohId } : { [recipe.lyeType]: selectedAlkaliId } }
  ), [pricedIngredients, calculations, selectedAlkaliId, selectedKohId, recipe.dualLye, recipe.lyeType]);

  const packagingSelection = useMemo(() => packagingItems.find(p => (p.id || p.firebaseId || p.key) === selectedPackagingId) || null, [packagingItems, selectedPackagingId]);
  const packagingUnitCost = packagingSelection ? convertMoney(getCostPerUnit(packagingSelection), packagingSelection.currencyCode || preferredCurrency, preferredCurrency) : num(recipe.packagingCostPerBar, 0);
  const packagingUnitType = packagingSelection ? getUnit(packagingSelection) : 'pcs';
  const packagingPerBarCost = packagingUnitCost;

  const alkaliItems = useMemo(() => ingredients.filter(i => {
    const name = (i.name || '').toLowerCase();
    const cat = (i.category || i.type || '').toLowerCase();
    return cat.includes('alkali') || name.includes('lye') || name.includes('naoh') || name.includes('koh');
  }), [ingredients]);

  const workingCost = num(recipe.laborCostPerBar, 0) * bars;
  const packagingCost = packagingPerBarCost * bars;

  const totalsCost = useMemo(() => buildTotals({
    material: materialCost,
    working: workingCost,
    packaging: packagingCost,
  }, bars), [materialCost, workingCost, packagingCost, bars]);

  const pricing = useMemo(() => computePricing(totalsCost.perBar, { markup: num(recipe.markupPercent, null) }), [totalsCost.perBar, recipe.markupPercent]);
  const recipeForPrint = normalizeRecipe({ ...recipe, items: calculations.oils, oils: calculations.oils,
    additives: calculations.additives, lyeRequired: calculations.lyeRequired, waterWeight: calculations.waterWeight,
    alkaliWeights: calculations.alkaliWeights, soapWeight: calculations.soapWeight, bars,
    totals: totalsCost, cost: { material: materialCost, working: workingCost, packaging: packagingCost }, pricing });

  // ============ HANDLERS ============
  const handleAddOil = useCallback((oil) => {
    const key = oil.key || oil.firebaseId || oil.name;
    if (recipe.items.some(i => i.key === key)) return;

    const newItem = {
      name: oil.name,
      key: key,
      pct: 0,
      sap: num(oil.sap),
      ...(oil.id ? { id: oil.id } : {}),
      catalogue: Boolean(oil.catalogue),
    };

    const updated = { ...recipe, items: [...recipe.items, newItem] };
    setRecipe(updated);
    isDirtyRef.current = true;
  }, [recipe]);

  const handleRemoveOil = useCallback((index) => {
    const updated = { ...recipe, items: recipe.items.filter((_, i) => i !== index) };
    setRecipe(updated);
    isDirtyRef.current = true;
  }, [recipe]);

  const handleOilChange = useCallback((index, field, value) => {
    const items = [...recipe.items];
    items[index] = { ...items[index], [field]: value, pct: value, percent: value };
    const updated = { ...recipe, items };
    setRecipe(updated);
    isDirtyRef.current = true;
  }, [recipe]);

  const handleFieldChange = useCallback((field, value) => {
    const updated = { ...recipe, [field]: value };
    setRecipe(updated);
    isDirtyRef.current = true;
  }, [recipe]);

  const handleNormalize = useCallback(() => {
    const total = calculations.totalPct;
    if (total === 0 || total === 100) return;
    
    let assigned = 0;
    const items = recipe.items.map((item, index) => {
      const pct = index === recipe.items.length - 1 ? Number((100 - assigned).toFixed(2)) : Number(((item.pct / total) * 100).toFixed(2));
      assigned += pct;
      return { ...item, pct, percent: pct };
    });
    const updated = { ...recipe, items };
    setRecipe(updated);
    isDirtyRef.current = true;
  }, [recipe, calculations.totalPct]);

  // Fetch recent recipes
  useEffect(() => {
    if (!user) { setRecentRecipes([]); return undefined; }
    let unsub = () => {};
    try {
      unsub = subscribeToUserCollection('recipes', (data) => {
        const sorted = data.sort((a, b) => {
          const aDate = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
          const bDate = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
          return bDate - aDate;
        });
        setRecentRecipes(sorted.slice(0, 5));
      });
    } catch (error) {
      console.error('Error loading recent recipes:', error);
    }
    return () => unsub();
  }, [user]);

  const loadRecipe = useCallback((selected) => {
    const normalized = normalizeRecipe(selected);
    setRecipe(previous => ({ ...previous, ...normalized }));
    setLoadedRecipeId(selected.id || null);
    setSelectedPackagingId(selected.packagingSelection?.id || '');
    setSelectedAlkaliId(selected.alkaliSelections?.NaOH || selected.alkaliSelection || '');
    setSelectedKohId(selected.alkaliSelections?.KOH || '');
    setMessage('');
    isDirtyRef.current = false;
  }, []);
  useEffect(() => { if (selectedRecipe) loadRecipe(selectedRecipe); }, [selectedRecipe, loadRecipe]);

  // Save recipe
  const handleSaveRecipe = async () => {
    if (!user) { setMessage('Sign in to save this recipe.'); return; }
    if (!hasValidRecipe) {
      setMessage(calculations.errors.join(' '));
      return;
    }
    
    setSaving(true);
    try {
      const recipeToSave = normalizeRecipe({
        ...recipe,
        oils: calculations.oils,
        items: calculations.oils,
        additives: calculations.additives,
        perOilDetails: calculations.oils,
        naohWeight: calculations.naohWeight,
        kohWeight: calculations.kohWeight,
        alkaliWeights: calculations.alkaliWeights,
        alkaliSelections: recipe.dualLye ? { NaOH: selectedAlkaliId, KOH: selectedKohId } : { [recipe.lyeType]: selectedAlkaliId },
        currencyCode: preferredCurrency,
        isTemplate: Boolean(recipe.createTemplate),
        lyeRequired: calculations.lyeRequired,
        waterWeight: calculations.waterWeight,
        soapWeight: calculations.soapWeight,
        properties: calculations.properties,
        fattyAcids: calculations.fattyAcids,
        cost: {
          material: materialCost,
          working: workingCost,
          packaging: packagingCost,
        },
        totals: totalsCost,
        pricing,
        bars,
        packagingSelection: packagingSelection ? {
          id: packagingSelection.id || packagingSelection.firebaseId || packagingSelection.key,
          name: packagingSelection.name,
          unit: packagingUnitType,
          costPerUnit: packagingUnitCost,
          quantityPerBar: 1,
        } : null,
        alkaliSelection: selectedAlkaliId || null,
        name: recipe.name?.trim() || `Recipe - ${new Date().toLocaleString()}`,
      });
      
      delete recipeToSave.id;
      delete recipeToSave.firebaseId;
      let recipeId = loadedRecipeId;
      if (recipeId) await updateUserDoc('recipes', recipeId, recipeToSave);
      else { const saved = await addUserDoc('recipes', recipeToSave); recipeId = saved.id; setLoadedRecipeId(recipeId); }
      if (!recipe.makePrivate || recipe.isPublic || recipe.publicRecipeId) {
        try {
          await httpsCallable(functions, 'publishUserRecipe')({ recipeId, published: !recipe.makePrivate });
          setRecipe(previous => ({ ...previous, isPublic: !recipe.makePrivate }));
        } catch (publishError) {
          setMessage('Recipe saved. Public sharing could not be updated; its previous visibility may remain. Try saving again after checking your connection.');
          isDirtyRef.current = false;
          return;
        }
      }
      isDirtyRef.current = false;
      setMessage("Recipe saved successfully.");
    } catch (error) {
      console.error("Error saving recipe:", error);
      setMessage("Unable to save recipe. Your changes are still here; please try again.");
    }
    finally { setSaving(false); }
  };

  // Convert unit display
  const getUnitLabel = () => recipe.unit === 'oz' ? 'Ounce(s)' : recipe.unit === 'lb' ? 'Pound(s)' : 'Gram(s)';
  const formatWeight = (w) => gramsToWeight(w, recipe.unit).toFixed(recipe.unit === 'g' ? 1 : 3);

  // One line that says what the advanced settings are set to, shown even while they are closed.
  const waterSummary = recipe.waterMethod === 'ratio' ? `water:lye ${recipe.waterLyeRatio}:1`
    : recipe.waterMethod === 'concentration' ? `${Number((recipe.lyeConcentration * 100).toFixed(1))}% lye concentration`
    : `water ${recipe.waterPercent}% of oils`;
  const advancedSummary = [
    recipe.dualLye ? `NaOH + KOH (${recipe.kohRatio}% KOH)` : recipe.lyeType,
    `${recipe.superfat}% superfat`,
    waterSummary,
    recipe.lyePurity !== 100 ? `${recipe.lyePurity}% purity` : null,
  ].filter(Boolean).join(' · ');
  const ounces = (grams) => (grams / 28.349523125).toFixed(2);
  const pounds = (grams) => (grams / 453.59237).toFixed(3);
  const totalPctOk = Math.abs(calculations.totalPct - 100) < 0.01;

  return (
    <><div className="calc">
      <div className="calc-main">
        {(message || calculations.errors.length > 0 || !user) && (
          <div className="calc-notices">
            {message && <p role="status" className="calc-message">{message}</p>}
            {calculations.errors.length > 0 && <div role="status" className="calc-errors"><ul>{calculations.errors.map(error => <li key={error}>{error}</li>)}</ul></div>}
            {!user && <p className="calc-hint"><a href="/login">Sign in</a> to save recipes. You can calculate and print without an account.</p>}
          </div>
        )}

        {/* 1. Batch size */}
        <section className="calc-step">
          <h2 className="calc-step-title"><span className="calc-step-no">1</span>Batch size</h2>
          <div className="calc-fields">
            <div className="calc-field">
              <label htmlFor="totalOilWeight">Oil Weight ({recipe.unit})</label>
              <input id="totalOilWeight" type="number" min="0.001" step="any"
                value={Number(gramsToWeight(recipe.totalOilWeight, recipe.unit).toFixed(4))}
                onChange={e => handleFieldChange('totalOilWeight', weightToGrams(e.target.value, recipe.unit))} />
              <span className="calc-hint">All the oils together.</span>
            </div>
            <div className="calc-field">
              <label htmlFor="barWeight">Bar Weight ({recipe.unit})</label>
              <input id="barWeight" type="number" min="0.001" step="any"
                value={Number(gramsToWeight(recipe.barWeight, recipe.unit).toFixed(4))}
                onChange={e => handleFieldChange('barWeight', weightToGrams(e.target.value, recipe.unit))} />
              <span className="calc-hint">{bars > 0 ? `Makes about ${bars} bars.` : 'Used to count your bars.'}</span>
            </div>
            <div className="calc-field">
              <label htmlFor="weightUnit">Weight Unit</label>
              <select id="weightUnit" value={recipe.unit} onChange={(e) => handleFieldChange('unit', e.target.value)}>
                <option value="g">Grams</option>
                <option value="oz">Ounces</option>
                <option value="lb">Pounds</option>
              </select>
            </div>
          </div>
        </section>

        {/* 2. Oils */}
        <section className="calc-step">
          <h2 className="calc-step-title"><span className="calc-step-no">2</span>Oils</h2>
          <p className="calc-hint calc-step-intro">
            Choose your oils, then give each one a share of the recipe so they add up to 100%.
            New to soapmaking? <Link to="/templates">Start from a tested recipe</Link>.
          </p>
          <div className="calc-oils">
            <div className="calc-oil-picker">
              <div className="oil-search">
                <input type="text" placeholder="Search oils…" aria-label="Search oils" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
              </div>
              <div className="oil-list">
                {filteredOils.map(oil => {
                  const isAdded = recipe.items.some(i => i.key === (oil.key || oil.firebaseId));
                  return (
                    <button
                      type="button"
                      key={oil.firebaseId || oil.key || oil.name}
                      className={`oil-item ${isAdded ? 'added' : ''}`}
                      onClick={() => !isAdded && handleAddOil(oil)}
                      aria-label={isAdded ? `${oil.name} added` : `Add ${oil.name}`}
                    >
                      <span>{oil.name}</span>
                      <span className="oil-item-action">{isAdded ? 'Added' : '+ Add'}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="calc-oil-table">
              <div className="calc-oil-head"><span>Oil</span><span>% of oils</span><span>{getUnitLabel()}</span><span /></div>
              {recipe.items.length === 0 ? (
                <p className="calc-empty">No oils yet. Choose oils from the list to start.</p>
              ) : (
                recipe.items.map((item, idx) => {
                  const calc = calculations.oils.find(o => o.key === item.key) || {};
                  return (
                    <div key={item.key || idx} className="calc-oil-row">
                      <span className="calc-oil-name">{item.name}</span>
                      <input
                        type="number"
                        min="0" max="100" step="any" aria-label={`${item.name} percentage`}
                        value={item.pct || ''}
                        onChange={(e) => handleOilChange(idx, 'pct', num(e.target.value))}
                        placeholder="%"
                      />
                      <span className="calc-oil-weight">{formatWeight(calc.weight)}</span>
                      <button type="button" className="calc-remove" onClick={() => handleRemoveOil(idx)} aria-label={`Remove ${item.name}`}>×</button>
                    </div>
                  );
                })
              )}
              {recipe.items.length > 0 && (
                <div className={`calc-oil-total ${totalPctOk ? '' : 'off'}`}>
                  <span>Total</span>
                  <strong>{calculations.totalPct.toFixed(1)}%</strong>
                  <span>{formatWeight(calculations.totalOilWeight)}</span>
                  <span />
                </div>
              )}
              {calculations.totalPct > 0 && !totalPctOk && (
                <button type="button" className="calc-normalize" onClick={handleNormalize}>
                  Make it 100% (now {calculations.totalPct.toFixed(1)}%)
                </button>
              )}
            </div>
          </div>
        </section>

        {/* 3. Fragrance, colour & additives */}
        <details className="calc-panel">
          <summary>
            <span className="calc-panel-title"><span className="calc-step-no">3</span>Fragrance, colour &amp; additives</span>
            <span className="calc-panel-summary">{recipe.additives?.length ? `${recipe.additives.length} added` : 'Optional'}</span>
          </summary>
          <div className="calc-panel-body">
            {recipe.additives?.length > 0 && (
              <div className="calc-additives">
                {recipe.additives.map((add, idx) => (
                  <div key={idx} className="calc-additive-row">
                    <span className="calc-tag">{add.type || 'additive'}</span>
                    <span>{add.name}</span>
                    <span>{add.amount} {add.unit}</span>
                    <button type="button" className="calc-remove" onClick={() => handleRemoveAdditive(idx)} aria-label={`Remove ${add.name}`}>×</button>
                  </div>
                ))}
              </div>
            )}
            {additiveOptions.length === 0 ? (
              <p className="calc-hint">
                {user ? <>Add fragrances, colours and other additives to your <Link to="/inventory">inventory</Link> to include them here.</>
                  : 'Sign in and add fragrances, colours and additives to your inventory to include them here.'}
              </p>
            ) : (
              <div className="calc-additive-form">
                <select
                  aria-label="Additive from inventory"
                  value={newAdditive.id}
                  onChange={(e) => {
                    const ing = additiveOptions.find(opt => opt.id === e.target.value || opt.firebaseId === e.target.value || opt.key === e.target.value);
                    if (ing) {
                      setNewAdditive({
                        id: ing.id || ing.firebaseId || ing.key,
                        name: ing.name,
                        unit: 'g',
                        key: ing.key,
                        type: (ing.category || ing.type || 'additive').toLowerCase().includes('frag') ? 'fragrance' :
                              (ing.category || ing.type || '').toLowerCase().includes('colour') ? 'colour' : 'additive',
                        amount: '',
                      });
                    } else {
                      setNewAdditive(prev => ({ ...prev, id: e.target.value }));
                    }
                  }}
                >
                  <option value="">Choose from your inventory</option>
                  {additiveOptions.map(opt => (
                    <option key={opt.id || opt.key || opt.firebaseId} value={opt.id || opt.key || opt.firebaseId}>
                      {opt.name} ({opt.unit || 'unit'})
                    </option>
                  ))}
                </select>
                <input
                  type="number" min="0" step="any" aria-label="Additive amount"
                  placeholder={`Amount${newAdditive.unit ? ` (${newAdditive.unit})` : ''}`}
                  value={newAdditive.amount}
                  onChange={(e) => setNewAdditive(prev => ({ ...prev, amount: e.target.value }))}
                />
                <button type="button" className="sl-btn sl-btn-secondary" onClick={handleAddAdditive}>Add</button>
              </div>
            )}
          </div>
        </details>

        {/* Advanced settings */}
        <details className="calc-panel">
          <summary>
            <span className="calc-panel-title">Advanced settings</span>
            <span className="calc-panel-summary">{advancedSummary}</span>
          </summary>
          <div className="calc-panel-body">
            <p className="calc-hint">The standard settings suit most bar soaps. Change them only if you know you need to.</p>
            <div className="calc-fields">
              <div className="calc-field">
                <label htmlFor="lyeType">Lye Type</label>
                <select id="lyeType" value={recipe.lyeType} onChange={(e) => handleFieldChange('lyeType', e.target.value)}>
                  <option value="NaOH">NaOH (bar soap)</option>
                  <option value="KOH">KOH (liquid soap)</option>
                </select>
              </div>
              <div className="calc-field">
                <label htmlFor="superfat">Superfat %</label>
                <div className="range-input">
                  <input id="superfat" type="range" min={0} max={25} value={recipe.superfat}
                    onChange={(e) => handleFieldChange('superfat', num(e.target.value))} />
                  <span>{recipe.superfat}%</span>
                </div>
                <span className="calc-hint">Oil left over for a milder bar. 5% is typical.</span>
              </div>
            </div>

            <div className="calc-checks">
              <div className="checkbox-row">
                <input type="checkbox" id="lyePurity" checked={recipe.lyePurity !== 100}
                  onChange={() => handleFieldChange('lyePurity', recipe.lyePurity === 100 ? 97 : 100)} />
                <label htmlFor="lyePurity">Lye Purity %</label>
                {recipe.lyePurity !== 100 && (
                  <input type="number" min="0.1" max="100" step="any" aria-label="Lye purity percentage" className="small-input"
                    value={recipe.lyePurity} onChange={(e) => handleFieldChange('lyePurity', num(e.target.value, 97))} />
                )}
              </div>
              <div className="checkbox-row">
                <input type="checkbox" id="dualLye" checked={recipe.dualLye} onChange={(e) => handleFieldChange('dualLye', e.target.checked)} />
                <label htmlFor="dualLye">Dual Lye (NaOH + KOH)</label>
              </div>
              {recipe.dualLye && (
                <div className="calc-fields">
                  <div className="calc-field">
                    <label htmlFor="kohRatio">KOH share (%)</label>
                    <input id="kohRatio" type="number" min="0" max="100" step="any" value={recipe.kohRatio}
                      onChange={e => handleFieldChange('kohRatio', num(e.target.value))} />
                  </div>
                  <div className="calc-field">
                    <label htmlFor="kohPurity">KOH Purity (%)</label>
                    <input id="kohPurity" type="number" min="0.1" max="100" step="any" value={recipe.kohPurity ?? recipe.lyePurity}
                      onChange={e => handleFieldChange('kohPurity', num(e.target.value))} />
                  </div>
                </div>
              )}
            </div>

            <fieldset className="calc-water">
              <legend>Water</legend>
              <div className="calc-water-modes">
                <label><input type="radio" name="waterMethod" checked={!['ratio', 'concentration'].includes(recipe.waterMethod)}
                  onChange={() => handleFieldChange('waterMethod', 'percent')} /> % of oil weight</label>
                <label><input type="radio" name="waterMethod" checked={recipe.waterMethod === 'ratio'}
                  onChange={() => handleFieldChange('waterMethod', 'ratio')} /> Water:Lye Ratio</label>
                <label><input type="radio" name="waterMethod" checked={recipe.waterMethod === 'concentration'}
                  onChange={() => handleFieldChange('waterMethod', 'concentration')} /> Lye Concentration %</label>
              </div>
              <div className="calc-fields">
                {!['ratio', 'concentration'].includes(recipe.waterMethod) && (
                  <div className="calc-field">
                    <label htmlFor="waterPercent">% of Oil Weight</label>
                    <input id="waterPercent" type="number" value={recipe.waterPercent}
                      onChange={(e) => handleFieldChange('waterPercent', num(e.target.value))} />
                  </div>
                )}
                {recipe.waterMethod === 'ratio' && (
                  <div className="calc-field">
                    <label htmlFor="waterRatioValue">Water:lye ratio</label>
                    <input id="waterRatioValue" type="number" min="0.01" step="any" value={recipe.waterLyeRatio}
                      onChange={e => handleFieldChange('waterLyeRatio', num(e.target.value))} />
                  </div>
                )}
                {recipe.waterMethod === 'concentration' && (
                  <div className="calc-field">
                    <label htmlFor="concentrationValue">Concentration (%)</label>
                    <input id="concentrationValue" type="number" min="0.1" max="99.9" step="any"
                      value={Number((recipe.lyeConcentration * 100).toFixed(4))}
                      onChange={e => handleFieldChange('lyeConcentration', num(e.target.value) / 100)} />
                  </div>
                )}
                <div className="calc-field">
                  <label htmlFor="waterDiscount">Water Discount %</label>
                  <input id="waterDiscount" type="number" min="0" max="99" value={recipe.waterDiscount || 0}
                    onChange={e => handleFieldChange('waterDiscount', num(e.target.value))} />
                </div>
              </div>
              <span className="calc-hint">A lye concentration of 28–35% is common. Less water speeds up trace and drying.</span>
            </fieldset>
          </div>
        </details>

        {/* Name and save */}
        <section className="calc-step">
          <h2 className="calc-step-title">Name and save</h2>
          <div className="calc-field">
            <label htmlFor="recipeName">Recipe name</label>
            <input id="recipeName" type="text" value={recipe.name} onChange={(e) => handleFieldChange('name', e.target.value)}
              placeholder="e.g. Lavender everyday bar" />
          </div>
          <div className="calc-field">
            <label htmlFor="recipeNotes">Notes and instructions</label>
            <textarea id="recipeNotes" value={recipe.notes} onChange={(e) => handleFieldChange('notes', e.target.value)}
              placeholder="Method, temperatures, what to try next time…" rows={4} />
          </div>
          <div className="calc-checks">
            <div className="checkbox-row">
              <input type="checkbox" id="makePrivate" checked={recipe.makePrivate} onChange={(e) => handleFieldChange('makePrivate', e.target.checked)} />
              <label htmlFor="makePrivate">Keep private (untick to share it in the Recipe Collection)</label>
            </div>
            <div className="checkbox-row">
              <input type="checkbox" id="createTemplate" checked={recipe.createTemplate} onChange={(e) => handleFieldChange('createTemplate', e.target.checked)} />
              <label htmlFor="createTemplate">Also save as a template</label>
            </div>
          </div>
          <div className="calc-actions">
            <button type="button" className="sl-btn sl-btn-primary" onClick={handleSaveRecipe} disabled={saving || !hasValidRecipe || !user}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button type="button" className="sl-btn sl-btn-secondary" onClick={() => setShowPrint(true)} disabled={!hasValidRecipe}>Print</button>
            {user && <span className="calc-hint">Save the recipe to add photos.</span>}
          </div>
        </section>
      </div>

      {/* Results */}
      <aside className="calc-results">
        <div className="calc-results-inner">
          <h2 className="calc-results-title">Your results</h2>
          <p className="calc-hint">{recipe.items.length ? `${bars} bars of ${formatWeight(recipe.barWeight)} ${recipe.unit}` : 'Add oils to see how much lye and water you need.'}</p>
          {calculations.warnings?.length > 0 && (
            <div role="status" className="calc-warnings">
              <strong>Check before you make this batch:</strong>
              <ul>{calculations.warnings.map(warning => <li key={warning}>{warning}</li>)}</ul>
            </div>
          )}
          <table className="calc-results-table">
            <tbody>
              <tr><th>Lye <span>{calculations.lyeLabel}</span></th><td>{formatWeight(calculations.lyeRequired)} {recipe.unit}</td></tr>
              <tr><th>Water <span>{calculations.waterLabel}</span></th><td>{formatWeight(calculations.waterWeight)} {recipe.unit}</td></tr>
              <tr><th>Oils</th><td>{formatWeight(calculations.totalOilWeight)} {recipe.unit}</td></tr>
              {calculations.additives.map((additive, index) => (
                <tr key={`${additive.id || additive.name}-${index}`}><th>{additive.name}</th><td>{formatWeight(additive.grams)} {recipe.unit}</td></tr>
              ))}
              <tr className="calc-total"><th>Total batch</th><td>{formatWeight(calculations.soapWeight)} {recipe.unit}</td></tr>
            </tbody>
          </table>
          <p className="calc-safety-line">Always add lye to water, never water to lye.</p>

          <details className="calc-more">
            <summary>More detail</summary>
            <table className="calc-detail-table">
              <thead><tr><th>Ingredient</th><th>{getUnitLabel()}</th><th>Ounces</th><th>Pounds</th><th>Batch %</th></tr></thead>
              <tbody>
                <tr><td>Lye</td><td>{formatWeight(calculations.lyeRequired)}</td><td>{ounces(calculations.lyeRequired)}</td><td>{pounds(calculations.lyeRequired)}</td><td>{calculations.lyeBatchPct.toFixed(0)}%</td></tr>
                <tr><td>Water</td><td>{formatWeight(calculations.waterWeight)}</td><td>{ounces(calculations.waterWeight)}</td><td>{pounds(calculations.waterWeight)}</td><td>{calculations.waterBatchPct.toFixed(0)}%</td></tr>
                <tr><td>Oils</td><td>{formatWeight(calculations.totalOilWeight)}</td><td>{ounces(calculations.totalOilWeight)}</td><td>{pounds(calculations.totalOilWeight)}</td><td>{calculations.oilBatchPct.toFixed(0)}%</td></tr>
                <tr className="calc-total"><td>Total</td><td>{formatWeight(calculations.soapWeight)}</td><td>{ounces(calculations.soapWeight)}</td><td>{pounds(calculations.soapWeight)}</td><td>100%</td></tr>
              </tbody>
            </table>
            <div className="calc-values">
              <div className="cv-row"><span>Superfat</span><span>{recipe.superfat}%</span></div>
              <div className="cv-row"><span>Lye concentration</span><span>{calculations.actualLyeConc.toFixed(1)}%</span></div>
              <div className="cv-row"><span>Water:lye ratio</span><span>{calculations.waterWeight > 0 && calculations.lyeRequired > 0
                ? (calculations.waterWeight / calculations.lyeRequired).toFixed(2) : '0'}:1</span></div>
              <div className="cv-row"><span>Soap type</span><span>{recipe.lyeType === 'NaOH' ? 'Bar' : 'Liquid'}</span></div>
              {recipe.dualLye && <><div className="cv-row"><span>NaOH</span><span>{formatWeight(calculations.naohWeight)} {recipe.unit}</span></div>
                <div className="cv-row"><span>KOH</span><span>{formatWeight(calculations.kohWeight)} {recipe.unit}</span></div></>}
            </div>
          </details>

          <section className="calc-qualities">
            <h3>Bar qualities</h3>
            <p className="calc-hint">Estimates for known oils; the shaded band is the usual range. They don’t replace testing. {calculations.propertyCoverage < 99.99 ? 'Some selected oils have no property data.' : ''}</p>
            <div className="properties-chart">
              {Object.entries(calculations.properties).slice(0, 6).map(([prop, value]) => (
                <PropertyBar key={prop} name={prop} value={value} />
              ))}
              {Object.keys(calculations.properties).length === 0 && <p className="calc-hint">Add oils to see how hard, cleansing and conditioning the bar will be.</p>}
            </div>
          </section>

          <details className="calc-more">
            <summary>Costs &amp; pricing <span>{formatMoney(totalsCost.perBar)} per bar</span></summary>
            <p className="calc-hint">Estimated from your inventory prices. Unpriced catalogue oils and volume-priced ingredients without a density are left out. Packaging is one item per bar.</p>
            <div className="calc-values">
              <div className="cv-row"><span>Material cost</span><span>{formatMoney(materialCost)}</span></div>
              <div className="cv-row">
                <span>Packaging</span>
                <select aria-label="Packaging item" value={selectedPackagingId} onChange={(e) => setSelectedPackagingId(e.target.value)}>
                  <option value="">Select packaging item</option>
                  {packagingItems.filter(p => ['pcs', 'pc', 'piece', 'pieces', 'unit', 'units'].includes((p.unit || 'pcs').toLowerCase())).map(p => (
                    <option key={p.id || p.firebaseId || p.key} value={p.id || p.firebaseId || p.key}>{p.name} ({p.unit || 'pcs'})</option>
                  ))}
                </select>
              </div>
              <div className="cv-row">
                <span>{recipe.dualLye ? 'NaOH inventory' : `${recipe.lyeType} inventory`}</span>
                <select aria-label="Alkali from inventory" value={selectedAlkaliId} onChange={(e) => setSelectedAlkaliId(e.target.value)}>
                  <option value="">Select alkali from inventory</option>
                  {alkaliItems.filter(a => recipe.dualLye || recipe.lyeType === 'NaOH' ? !/koh|potassium/i.test(a.name || '') : /koh|potassium/i.test(a.name || '')).map(a => (
                    <option key={a.id || a.firebaseId || a.key} value={a.id || a.firebaseId || a.key}>{a.name} ({a.unit || 'kg'})</option>
                  ))}
                </select>
              </div>
              {recipe.dualLye && <div className="cv-row"><span>KOH inventory</span><select aria-label="KOH from inventory" value={selectedKohId} onChange={e => setSelectedKohId(e.target.value)}>
                <option value="">Select KOH</option>{alkaliItems.filter(a => /koh|potassium/i.test(a.name || '')).map(a =>
                  <option key={a.id || a.key} value={a.id || a.firebaseId || a.key}>{a.name}</option>)}
              </select></div>}
              <div className="cv-row">
                <span>Labour per bar</span>
                <input type="number" value={recipe.laborCostPerBar} onChange={(e) => handleFieldChange('laborCostPerBar', num(e.target.value, 0))}
                  className="small-input" aria-label="Labor cost per bar" />
              </div>
              <div className="cv-row"><span>Packaging total</span><span>{formatMoney(packagingCost)}</span></div>
              <div className="cv-row"><span>Labour total</span><span>{formatMoney(workingCost)}</span></div>
              <div className="cv-row"><span>Total cost</span><span>{formatMoney(totalsCost.totalCost)}</span></div>
              <div className="cv-row"><span>Bars</span><span>{bars}</span></div>
              <div className="cv-row"><span>Cost per bar</span><span>{formatMoney(totalsCost.perBar)}</span></div>
              <div className="cv-row">
                <span>Markup %</span>
                <input type="number" value={recipe.markupPercent} onChange={(e) => handleFieldChange('markupPercent', num(e.target.value, 0))}
                  className="small-input" aria-label="Markup percent" />
              </div>
              <div className="cv-row"><span>Suggested price</span><span>{formatMoney(pricing.sellingPrice)}</span></div>
              <div className="cv-row"><span>Profit per bar</span><span>{formatMoney(pricing.profit)}</span></div>
              <div className="cv-row"><span>Margin</span><span>{pricing.margin}%</span></div>
            </div>
          </details>

          {recentRecipes.length > 0 && (
            <section className="calc-recent">
              <h3>Recent recipes</h3>
              {recentRecipes.map((r, i) => (
                <button type="button" key={r.firebaseId || i} className="calc-recent-item" onClick={() => loadRecipe(r)}>
                  {r.name || `Recipe ${i + 1}`}
                </button>
              ))}
            </section>
          )}
        </div>
      </aside>
    </div>{showPrint && <RecipePrintSheet recipe={recipeForPrint} onClose={() => setShowPrint(false)} formatMoney={formatMoney} />}</>
  );
}

// Property Bar Component
function PropertyBar({ name, value }) {
  const ranges = {
    Hardness: [29, 54],
    Cleansing: [12, 22],
    Conditioning: [44, 69],
    Bubbly: [14, 46],
    Creamy: [16, 48],
    Longevity: [25, 50],
  };
  
  const range = ranges[name] || [0, 100];
  const isInRange = value >= range[0] && value <= range[1];
  const displayValue = Math.min(100, Math.max(0, value));
  
  return (
    <div className="property-bar">
      <div className="pb-header">
        <span className="pb-name">{name}</span>
        <span className={`pb-value ${isInRange ? 'in-range' : 'out-range'}`}>{value.toFixed(0)}</span>
      </div>
      <div className="pb-track">
        <div className="pb-range" style={{ left: `${range[0]}%`, width: `${range[1] - range[0]}%` }} />
        <div className={`pb-fill ${isInRange ? 'good' : 'warn'}`} style={{ width: `${displayValue}%` }} />
      </div>
    </div>
  );
}

